import React, { useEffect, useRef, useState } from 'react';
import { fetchNotifications, markNotificationRead } from './api';

// 2026-08-16 request: "when a user gets a message from another user make a popup notice ...
// no matter where they are using the app or website". Mounted unconditionally in App.jsx's
// authenticated branch (same idea as PowerOutageBanner, just gated to only after login since
// notifications require a session) so it fires regardless of which page is on screen.
//
// Polls GET /user/notifications (same 20s cadence as PowerOutageBanner) rather than a
// websocket/SSE push -- matches every other "check something regardless of what screen
// you're on" mechanism already in this app (Pi heartbeat, power-status banner). The FIRST
// poll after mount only records what's already unread, it never pops up for a backlog of
// old messages from before the page loaded -- only a message that arrives newer than that
// baseline triggers the popup.
const POLL_MS = 20000;

const MessagePopupNotice = ({ onOpenMessages }) => {
  const [popup, setPopup] = useState(null); // { id, message, link } | null
  const seenIdsRef = useRef(null); // null until the first poll establishes a baseline

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      const list = await fetchNotifications().catch(() => null);
      if (!list || cancelled) return;
      const unreadMessages = list.filter((n) => n.type === 'chat_message' && !n.is_read);

      if (seenIdsRef.current === null) {
        // Baseline pass: whatever's already unread doesn't pop up retroactively.
        seenIdsRef.current = new Set(unreadMessages.map((n) => n.id));
        return;
      }

      const fresh = unreadMessages.find((n) => !seenIdsRef.current.has(n.id));
      unreadMessages.forEach((n) => seenIdsRef.current.add(n.id));
      if (fresh) setPopup(fresh);
    };
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (!popup) return null;

  const dismiss = () => {
    markNotificationRead(popup.id).catch(() => {});
    setPopup(null);
  };

  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        top: '68px',
        right: '20px',
        zIndex: 10001,
        width: '300px',
        padding: '16px',
        background: 'rgba(10,14,12,0.96)',
        border: '1px solid rgba(0,255,136,0.4)',
        borderRadius: '8px',
        boxShadow: '0 0 24px rgba(0,255,136,0.2)',
        backdropFilter: 'blur(8px)',
        fontFamily: 'Rajdhani, sans-serif',
      }}
    >
      <div
        style={{
          fontFamily: 'Orbitron, monospace',
          fontSize: '10px',
          fontWeight: 900,
          letterSpacing: '0.15em',
          color: '#00ff88',
          marginBottom: '8px',
        }}
      >
        ✉ NEW MESSAGE
      </div>
      <div style={{ fontSize: '13px', color: '#c8f5db', marginBottom: '14px', lineHeight: 1.4 }}>
        {popup.message}
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          type="button"
          onClick={() => {
            markNotificationRead(popup.id).catch(() => {});
            setPopup(null);
            onOpenMessages?.();
          }}
          style={{
            flex: 1, padding: '9px 12px', background: 'rgba(0,255,136,0.12)',
            border: '1px solid rgba(0,255,136,0.4)', borderRadius: '6px', color: '#00ff88',
            fontWeight: '900', fontSize: '11px', letterSpacing: '0.08em', cursor: 'pointer',
            fontFamily: 'Orbitron, monospace',
          }}
        >
          VIEW
        </button>
        <button
          type="button"
          onClick={dismiss}
          style={{
            padding: '9px 12px', background: 'transparent', border: '1px solid rgba(0,255,136,0.15)',
            borderRadius: '6px', color: '#5a8a72', fontWeight: '700', fontSize: '11px',
            letterSpacing: '0.06em', cursor: 'pointer', fontFamily: 'Rajdhani, sans-serif',
          }}
        >
          DISMISS
        </button>
      </div>
    </div>
  );
};

export default MessagePopupNotice;
